import { useRef, useEffect, useCallback, useState } from "react";
import { Grid, Typography, Box, CircularProgress } from "@mui/material";
import { useProductCatalogContext } from "@/context/ProductCatalogContext";
import { ProductCard } from "./ProductCard";

export const ProductGrid = () => {
  const { catalog, loading, loadMoreProducts, hasMoreProducts } =
    useProductCatalogContext();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  // Intersection Observer for infinite scrolling
  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        !loading &&
        !isLoadingMore &&
        hasMoreProducts
      ) {
        setIsLoadingMore(true);
        await loadMoreProducts();
        setIsLoadingMore(false);
      }
    },
    [loading, isLoadingMore, hasMoreProducts, loadMoreProducts],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "0px 0px 400px 0px", // Load more when within 400px of the bottom
      threshold: 0.1,
    });

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  return (
    <Grid
      id="product-grid"
      data-cypress="products-grid"
      container
      alignItems="stretch"
      justifyContent="center"
      margin="auto"
      columns={{ xs: 12, sm: 12, md: 9 }}
      sx={{
        marginTop: { sm: "180px", xs: "100px" },
        width: { xs: "100%", sm: "100%", md: "100%", lg: "100%" },
        maxWidth: { xs: "100%", sm: 600, md: 900, lg: 1200 },
        padding: { xs: "0 12px", sm: "0 16px", md: "0 24px" },
      }}
    >
      {loading && !catalog?.length ? (
        <Typography variant="h6" sx={{ margin: "auto", paddingTop: 4 }}>
          Loading Results...
        </Typography>
      ) : (catalog?.length ?? 0) > 0 ? (
        <>
          {catalog?.map((product, index) => (
            <Grid
              key={product.product_id + index}
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={{ padding: { xs: "8px 0", sm: 1, md: 1 } }}
            >
              <ProductCard product={product} />
            </Grid>
          ))}

          {/* Observer target element - when this becomes visible, load more products */}
          <Grid
            size={{ xs: 12 }}
            ref={observerTarget}
            sx={{ height: 20, mt: 2, mb: 4 }}
          >
            {(isLoadingMore || (loading && (catalog?.length ?? 0) > 0)) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <CircularProgress size={30} />
              </Box>
            )}

            {!hasMoreProducts && (catalog?.length ?? 0) > 0 && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#666" }}
              >
                No more products to load, please make a more specific search.
              </Typography>
            )}
          </Grid>
        </>
      ) : (
        <Typography variant="h6" sx={{ margin: "auto", paddingTop: 4 }}>
          No Results
        </Typography>
      )}
    </Grid>
  );
};
